import React, { useState, useRef } from "react";
import api from "@/lib/api";
import { Upload, FileSpreadsheet, Loader2, X, CheckCircle2, AlertTriangle, ArrowRight } from "lucide-react";

export default function ImportWizard({ onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [form, setForm] = useState({code:"", title:"", subtitle:"", description:"", category:"Management", author:"", duration_hours:1, cover_color:"#185FA5", published:true});
  const fileRef = useRef(null);

  const onPick = async (f) => {
    if (!f) return;
    setFile(f); setErr(""); setPreview(null); setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/admin/import/preview", fd, { headers: {"Content-Type": "multipart/form-data"} });
      setPreview(data);
      // Auto-suggest title from first block if not set
      if (!form.title && data.blocks?.length) {
        setForm(s => ({...s, title: data.blocks.length === 1 ? data.blocks[0].title : "Imported course"}));
      }
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not parse file");
    } finally { setBusy(false); }
  };

  const onDrop = (e) => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f) onPick(f); };

  const commit = async () => {
    setErr(""); setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      Object.entries(form).forEach(([k,v]) => fd.append(k, v));
      const { data } = await api.post("/admin/import/commit", fd, { headers: {"Content-Type": "multipart/form-data"} });
      onImported(data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Import failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="import-modal">
      <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[92vh] overflow-y-auto" onClick={e=>e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-stone-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <div className="flex items-center gap-2">
            <FileSpreadsheet size={20} className="text-emerald-700"/>
            <div className="tw-display text-xl font-bold">Import course from Excel</div>
          </div>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-stone-100" data-testid="import-close-btn"><X size={18}/></button>
        </div>

        <div className="p-6">
          {!preview && (
            <>
              <div
                onDragOver={e=>e.preventDefault()}
                onDrop={onDrop}
                onClick={()=>fileRef.current?.click()}
                className="border-2 border-dashed border-stone-300 rounded-xl p-10 text-center cursor-pointer hover:border-stone-900 transition"
                data-testid="import-dropzone"
              >
                <Upload size={32} className="mx-auto text-stone-400"/>
                <div className="mt-3 font-semibold">Drop a .xlsx file here, or click to browse</div>
                <div className="text-sm text-stone-500 mt-1">Tipps Adams outline format <em>or</em> tabular (Block Title / Lesson Title columns) — both supported</div>
                <input ref={fileRef} type="file" accept=".xlsx,.xlsm" className="hidden" onChange={e=>onPick(e.target.files?.[0])} data-testid="import-file-input"/>
              </div>
              {busy && <div className="mt-4 text-stone-600 text-sm flex items-center gap-2"><Loader2 size={14} className="animate-spin"/> Parsing…</div>}
              {err && <div className="mt-4 text-red-600 text-sm" data-testid="import-error">{err}</div>}

              <details className="mt-6 text-sm">
                <summary className="cursor-pointer font-semibold text-stone-700">Accepted formats</summary>
                <div className="mt-3 text-stone-600 space-y-2">
                  <p><strong>1. Outline format (Tipps Adams style)</strong> — first column has rows like <code>Block 1 — Introduction</code>, <code>1.1  Lesson title</code>; second column has <code>1.1.1  Objective text</code>.</p>
                  <p><strong>2. Tabular format</strong> — first row is headers. Required columns: <code>Block Title</code>, <code>Lesson Title</code>. Optional: <code>Block Order</code>, <code>Block Summary</code>, <code>Block Learning Outcome</code>, <code>Lesson Order</code>, <code>Objectives</code>, <code>Video URL</code>, <code>Slides URL</code>, <code>Reading</code>, <code>Interactive</code>.</p>
                  <p className="text-xs text-stone-500">Quizzes are not in the import — author them per-lesson afterwards with the Quiz Editor or AI generation.</p>
                </div>
              </details>
            </>
          )}

          {preview && (
            <>
              <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-xl p-4" data-testid="import-preview-banner">
                <CheckCircle2 size={20} className="text-emerald-700"/>
                <div>
                  <div className="font-bold">Parsed successfully</div>
                  <div className="text-sm text-stone-700">{preview.block_count} blocks · {preview.lesson_count} lessons from <strong>{file.name}</strong></div>
                </div>
              </div>

              {preview.warnings?.length > 0 && (
                <div className="mt-3 bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm">
                  <div className="flex items-center gap-2 font-semibold text-amber-800"><AlertTriangle size={14}/> Warnings</div>
                  <ul className="list-disc list-inside mt-1 text-stone-700">{preview.warnings.map((w,i)=><li key={i}>{w}</li>)}</ul>
                </div>
              )}

              <div className="tw-mono-label mt-6 mb-2">COURSE METADATA</div>
              <div className="grid sm:grid-cols-2 gap-3 bg-stone-50 border border-stone-200 rounded-xl p-4">
                <div><label className="tw-mono-label block mb-1">Course code</label><input className="tw-input" placeholder="TA0003" value={form.code} onChange={e=>setForm({...form,code:e.target.value.toUpperCase()})} required data-testid="import-code-input"/></div>
                <div><label className="tw-mono-label block mb-1">Title</label><input className="tw-input" value={form.title} onChange={e=>setForm({...form,title:e.target.value})} required data-testid="import-title-input"/></div>
                <div className="sm:col-span-2"><label className="tw-mono-label block mb-1">Subtitle</label><input className="tw-input" value={form.subtitle} onChange={e=>setForm({...form,subtitle:e.target.value})}/></div>
                <div className="sm:col-span-2"><label className="tw-mono-label block mb-1">Description</label><textarea className="tw-input" rows={2} value={form.description} onChange={e=>setForm({...form,description:e.target.value})}/></div>
                <div><label className="tw-mono-label block mb-1">Category</label><input className="tw-input" value={form.category} onChange={e=>setForm({...form,category:e.target.value})}/></div>
                <div><label className="tw-mono-label block mb-1">Author</label><input className="tw-input" value={form.author} onChange={e=>setForm({...form,author:e.target.value})}/></div>
                <div><label className="tw-mono-label block mb-1">Duration (hours)</label><input className="tw-input" type="number" step="0.1" value={form.duration_hours} onChange={e=>setForm({...form,duration_hours:e.target.value})}/></div>
                <div><label className="tw-mono-label block mb-1">Cover color</label><input className="tw-input" type="color" value={form.cover_color} onChange={e=>setForm({...form,cover_color:e.target.value})}/></div>
                <label className="sm:col-span-2 text-sm flex items-center gap-2"><input type="checkbox" checked={form.published} onChange={e=>setForm({...form,published:e.target.checked})}/> Publish immediately (visible in catalog)</label>
              </div>

              <div className="tw-mono-label mt-6 mb-2">PREVIEW · WHAT WILL BE CREATED</div>
              <div className="bg-stone-50 border border-stone-200 rounded-xl p-4 max-h-72 overflow-y-auto" data-testid="import-preview-tree">
                {preview.blocks.map((b,i)=>(
                  <div key={i} className="py-1.5">
                    <div className="font-semibold tw-display">Block {b.order} — {b.title}</div>
                    {b.learning_outcome && <div className="text-xs text-stone-500 ml-3 italic">↳ {b.learning_outcome.slice(0,140)}{b.learning_outcome.length>140?"…":""}</div>}
                    <ul className="ml-5 mt-1 text-sm text-stone-700">
                      {b.lessons.map((l,j)=>(
                        <li key={j} className="py-0.5">
                          <span className="font-medium">{b.order}.{l.order} {l.title}</span>
                          {l.objectives.length > 0 && <span className="text-xs text-stone-500"> · {l.objectives.length} objective{l.objectives.length>1?"s":""}</span>}
                          {l.video_url && <span className="text-xs text-blue-600"> · video</span>}
                          {l.reading && <span className="text-xs text-emerald-700"> · reading</span>}
                          {l.interactive && <span className="text-xs text-amber-700"> · interactive</span>}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>

              {err && <div className="mt-3 text-red-600 text-sm" data-testid="import-commit-error">{err}</div>}

              <div className="mt-6 flex items-center justify-between gap-3 flex-wrap">
                <button onClick={()=>{setPreview(null); setFile(null); setErr("");}} className="text-sm text-stone-600 underline" data-testid="import-pick-different">Pick a different file</button>
                <div className="flex gap-2">
                  <button onClick={onClose} className="tw-btn-outline text-sm">Cancel</button>
                  <button onClick={commit} disabled={busy || !form.code.trim() || !form.title.trim()} className="tw-btn-primary inline-flex items-center gap-2 text-sm" data-testid="import-commit-btn">
                    {busy ? <><Loader2 size={14} className="animate-spin"/> Importing…</> : <>Import {preview.lesson_count} lessons <ArrowRight size={14}/></>}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
